import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type ModalType = 'info' | 'success' | 'warning' | 'error' | 'danger';

interface ModalButton {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  onClick?: () => void | Promise<void>;
}

interface ModalOptions {
  title: string;
  message: string | ReactNode;
  type?: ModalType;
  confirmLabel?: string;
  cancelLabel?: string;
  showCancel?: boolean;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  buttons?: ModalButton[];
}

interface ModalState extends ModalOptions {
  isOpen: boolean;
  isLoading: boolean;
}

interface ModalContextType {
  showModal: (options: ModalOptions) => Promise<boolean>;
  showAlert: (title: string, message: string, type?: ModalType) => Promise<void>;
  showConfirm: (title: string, message: string, options?: Partial<ModalOptions>) => Promise<boolean>;
  showDanger: (title: string, message: string, confirmLabel?: string) => Promise<boolean>;
  hideModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    isLoading: false,
    title: '',
    message: '',
    type: 'info',
    showCancel: true,
  });

  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const showModal = useCallback((options: ModalOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
      setModalState({
        isOpen: true,
        isLoading: false,
        title: options.title,
        message: options.message,
        type: options.type || 'info',
        confirmLabel: options.confirmLabel || 'OK',
        cancelLabel: options.cancelLabel || 'Cancel',
        showCancel: options.showCancel !== false,
        onConfirm: options.onConfirm,
        onCancel: options.onCancel,
        buttons: options.buttons,
      });
    });
  }, []);

  const showAlert = useCallback((title: string, message: string, type: ModalType = 'info'): Promise<void> => {
    return showModal({
      title,
      message,
      type,
      showCancel: false,
      confirmLabel: 'OK',
    }).then(() => {});
  }, [showModal]);

  const showConfirm = useCallback((title: string, message: string, options?: Partial<ModalOptions>): Promise<boolean> => {
    return showModal({
      title,
      message,
      type: 'warning',
      showCancel: true,
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      ...options,
    });
  }, [showModal]);

  const showDanger = useCallback((title: string, message: string, confirmLabel = 'Delete'): Promise<boolean> => {
    return showModal({
      title,
      message,
      type: 'danger',
      showCancel: true,
      confirmLabel,
      cancelLabel: 'Cancel',
    });
  }, [showModal]);

  const hideModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  }, [resolvePromise]);

  const handleConfirm = useCallback(async () => {
    setModalState((prev) => ({ ...prev, isLoading: true }));
    try {
      if (modalState.onConfirm) {
        await modalState.onConfirm();
      }
      setModalState((prev) => ({ ...prev, isOpen: false, isLoading: false }));
      if (resolvePromise) {
        resolvePromise(true);
        setResolvePromise(null);
      }
    } catch (error) {
      setModalState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [modalState.onConfirm, resolvePromise]);

  const handleCancel = useCallback(() => {
    if (modalState.onCancel) {
      modalState.onCancel();
    }
    setModalState((prev) => ({ ...prev, isOpen: false }));
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  }, [modalState.onCancel, resolvePromise]);

  const getIconForType = (type: ModalType) => {
    switch (type) {
      case 'success':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        );
      case 'warning':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'error':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'danger':
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        );
      default:
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
    }
  };

  const getConfirmButtonVariant = (type: ModalType) => {
    if (type === 'danger') return 'btn-danger';
    if (type === 'error') return 'btn-danger';
    return 'btn-primary';
  };

  return (
    <ModalContext.Provider value={{ showModal, showAlert, showConfirm, showDanger, hideModal }}>
      {children}
      {modalState.isOpen && (
        <div className="confirm-modal-overlay" onClick={handleCancel}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`confirm-modal-icon confirm-modal-icon-${modalState.type}`}>
              {getIconForType(modalState.type || 'info')}
            </div>
            <div className="confirm-modal-content">
              <h3 className="confirm-modal-title">{modalState.title}</h3>
              <div className="confirm-modal-message">{modalState.message}</div>
            </div>
            <div className="confirm-modal-actions">
              {modalState.showCancel && (
                <button
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={modalState.isLoading}
                >
                  {modalState.cancelLabel}
                </button>
              )}
              <button
                className={`btn ${getConfirmButtonVariant(modalState.type || 'info')}`}
                onClick={handleConfirm}
                disabled={modalState.isLoading}
              >
                {modalState.isLoading ? (
                  <span className="confirm-modal-spinner" />
                ) : (
                  modalState.confirmLabel
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
